const verdictColors = {
    'Proceed': 'bg-green-100 text-green-800',
    'Reject': 'bg-red-100 text-red-800',
    'Incomplete': 'bg-yellow-100 text-yellow-800',
    'Review': 'bg-yellow-100 text-yellow-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    return typeof v === 'number' ? v.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
};

function DecisionMemoSection({ decisionMemo, evaluation }) {
    if (!decisionMemo || !decisionMemo.approvedAmount) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Decision Memo</h3>
                </div>
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                    Click "Compute DTI & Score" to generate the decision memo.
                </div>
            </div>
        );
    }

    const hardRules = [
        { label: 'KYC Verification', verdict: evaluation?.kyc?.kycVerdict || '' },
        { label: 'Credit Bureau', verdict: evaluation?.creditBureau?.bureauVerdict || '' },
        { label: 'Fraud & Compliance', verdict: evaluation?.fraud?.fraudVerdict || '' },
        { label: 'Guarantor Check', verdict: evaluation?.guarantor?.guarantorVerdict || '' },
        { label: 'DTI', verdict: evaluation?.dti?.dtiVerdict || '' },
    ];

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Decision Memo</h3>
                <VerdictBadge verdict={decisionMemo.eligibilityVerdict} />
            </div>

            {/* Eligibility Hard Rules */}
            <div className="px-4 pt-3 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Eligibility Hard Rules</span>
            </div>
            <div className="divide-y divide-gray-100">
                {hardRules.map(rule => (
                    <div key={rule.label} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-600">{rule.label}</span>
                        {rule.verdict ? (
                            <HardRuleBadge verdict={rule.verdict} />
                        ) : (
                            <span className="text-xs text-gray-400">Not evaluated</span>
                        )}
                    </div>
                ))}
            </div>

            {/* Loan Structure */}
            <div className="px-4 pt-4 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Loan Structure</span>
            </div>
            <div className="divide-y divide-gray-100">
                <Row label="Requested amount" value={`GHS ${fmt(decisionMemo.requestedAmount)}`} />
                <Row label="Approved amount (capped at 40% DTI)" value={`GHS ${fmt(decisionMemo.approvedAmount)}`} highlight />
                <Row label="Tenure" value={`${decisionMemo.tenure} months`} />
                <Row label="Annual rate (base + risk premium)" value={`${decisionMemo.annualRate}%`} />
                <Row label="Monthly repayment" value={`GHS ${fmt(decisionMemo.monthlyRepayment)}`} />
                <Row label="Total repayment" value={`GHS ${fmt(decisionMemo.totalRepayment)}`} />
            </div>

            {/* Fees & Disbursement */}
            <div className="px-4 pt-4 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fees & Disbursement</span>
            </div>
            <div className="divide-y divide-gray-100">
                <Row label="Processing fee (2%)" value={`GHS ${fmt(decisionMemo.processingFee)}`} />
                <Row label="Insurance fee (1%)" value={`GHS ${fmt(decisionMemo.insuranceFee)}`} />
                <div className="flex items-center justify-between px-4 py-3 bg-green-50">
                    <span className="text-sm font-semibold text-gray-700">Net disbursement</span>
                    <span className="text-sm font-bold text-green-700">GHS {fmt(decisionMemo.netDisbursement)}</span>
                </div>
            </div>

            {/* Approver Sign-off */}
            {decisionMemo.approverSignoff && (
                <div className="px-4 pt-4 pb-1 border-t border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approver Sign-off</span>
                </div>
            )}
            {decisionMemo.approverSignoff && (
                <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-gray-600">Relationship manager</span>
                    <span className="text-sm font-medium text-gray-900">{decisionMemo.approverSignoff}</span>
                </div>
            )}
        </div>
    );
}

function HardRuleBadge({ verdict }) {
    let color = 'bg-gray-100 text-gray-600';
    if (verdict === 'Pass' || verdict === 'Level 1 — Clear' || verdict === 'Proceed') color = 'bg-green-100 text-green-700';
    else if (verdict === 'Fail' || verdict.includes('Reject')) color = 'bg-red-100 text-red-700';
    else if (verdict === 'Review' || verdict.includes('Escalate') || verdict === 'Incomplete') color = 'bg-yellow-100 text-yellow-700';
    return <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${color}`}>{verdict}</span>;
}

function Row({ label, value, highlight }) {
    return (
        <div className={`flex items-center justify-between px-4 py-2.5 ${highlight ? 'bg-blue-50/50' : ''}`}>
            <span className="text-sm text-gray-600">{label}</span>
            <span className={`text-sm font-medium ${highlight ? 'font-bold text-[#1e3a6e]' : 'text-gray-900'}`}>{value}</span>
        </div>
    );
}

export default DecisionMemoSection;
